import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { withStopPropagation } from 'antd-mobile/es/utils/with-stop-propagation';
import { AppointmentPermissions } from '@bb/domain/dist/types/AppointmentPermissions';
import { VerticalSpace } from '@brokerbay/design-components';
import { Button, DescriptionText, Space, Icon, message } from 'components';
import {
  coopConfirmCancel,
  // API function name should follow the controller function name,
  // such as "coopDenyTimeChange" & "setStatusByCoop".
  coopDenyTimeChange,
  setStatusByCoop,
} from 'api/AppointmentController';
import AppointmentActionModalContext from 'contexts/modal/AppointmentActionModalContext';
import type { AppointmentActionModalContextType } from 'contexts/modal/types';
import {
  createFormatDateFunction,
  formatISODateStr,
  formatTimeStr,
} from 'utils';
import { useShowingsCalendarContext } from '../../ShowingsCalendarContext';
import { listingAgentNeedsAction, timeChangeNeedsAction } from '../../utils';
import { ES_DELAY, TIME_CHANGE_STATUSES } from '../../constants';
// For tour appts, instead of modifying "ActionType" which is used for
// request params as status field, simply use existing modal from tours components
import showConfirmDeleteTourModal from '../../../TourEditPage/ConfirmDeleteTourModal';
import { TourCancelType } from '../../../TourEditPage/types';

export type ActionsEntryProps = {
  appointment: AppointmentActionModalContextType['appointment'];
  isBuyer: boolean;
  canViewDetails: boolean;
  hasOneTourShowing?: boolean;
  hasOneTourItem?: boolean;
  hideActions?: boolean;
  permissions?: AppointmentPermissions;
  isMobile?: boolean;
  isTourItem?: boolean;
};

const ActionsEntry: React.FC<ActionsEntryProps> = ({
  canViewDetails,
  isBuyer,
  hasOneTourShowing,
  hasOneTourItem,
  appointment,
  hideActions,
  permissions,
  isMobile,
  isTourItem,
}) => {
  const {
    notify,
    time,
    listingContactTimeChange,
    keepOriginalAppointment,
    originalTime,
    originalTimeEnd,
    status,
    timeEnd,
    listing: { timezone } = {},
  } = appointment;
  const { openModal } = useContext(AppointmentActionModalContext);
  const { refetchPage } = useShowingsCalendarContext();
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const showActionButtons =
    !hideActions &&
    (isBuyer
      ? timeChangeNeedsAction({
          isBuyer: true,
          status,
          listingContactTimeChange,
        })
      : listingAgentNeedsAction({
          status,
          listingContactTimeChange,
        }));

  const canAccept = isBuyer || permissions?.confirm;
  const canDeny = isBuyer || permissions?.deny;

  const buyerConfirm = async () => {
    // For tour appointments, if the action is taken on last appointment
    // of the tour, open the accept time change suggestion modal from tour components.
    const dateStr = formatISODateStr(time, timezone);
    const originalDateStr = formatISODateStr(originalTime, timezone);
    if (hasOneTourShowing && dateStr !== originalDateStr) {
      // This is copied from TourEditContext
      showConfirmDeleteTourModal(
        // "appointment" used for buyer is `(BuyerShowing & { passed?: boolean })`
        (appointment as any).appointmentTourId as string,
        TourCancelType.SUGGESTION_ACCEPT,
        undefined,
        undefined,
        async () => {
          try {
            setLoading(true);
            await setStatusByCoop(appointment.id, {
              status: 'confirm',
            });
            message.success('Suggested time accepted');
          } catch (err) {
            message.error('Error confirming showing');
          } finally {
            setLoading(false);
            refetchPage(ES_DELAY.LARGE);
          }
        },
      );
      return;
    }

    try {
      setLoading(true);
      await coopConfirmCancel({
        appointmentId: appointment.id,
        status: 'confirm',
      });
      message.success('Showing confirmed');
    } catch (err) {
      message.error('Error confirming showing');
    } finally {
      setLoading(false);
      refetchPage(ES_DELAY.LARGE);
    }
  };

  const buyerDeny = async () => {
    // For tour appointments, if the action is taken on last tour item
    // of the tour, open the deny time change suggestion + cancel tour modal
    // from tour components.
    if (hasOneTourItem && !keepOriginalAppointment) {
      showConfirmDeleteTourModal(
        // "appointment" used for buyer is `(BuyerShowing & { passed?: boolean })`
        (appointment as any).appointmentTourId as string,
        TourCancelType.SUGGESTION_CANCEL_LAST,
        undefined,
        undefined,
        async () => {
          try {
            setLoading(true);
            await coopDenyTimeChange(appointment.id, {
              notify,
              isPending: false,
            });
          } catch (err) {
            // Do nothing, as showConfirmDeleteTourModal itself will send message alert
          } finally {
            setLoading(false);
            refetchPage(ES_DELAY.LARGE);
          }
        },
      );
      return;
    }

    openModal({
      agent: 'coop',
      action: 'deny_coop',
      appointment,
      onConfirm: () => refetchPage(ES_DELAY.LARGE),
    });
  };

  const laConfirm = async () => {
    openModal({
      agent: 'listing',
      action: 'confirm',
      appointment,
      onConfirm: refetchPage,
    });
  };

  const laDeny = async () => {
    openModal({
      agent: 'listing',
      action: 'deny',
      appointment,
      onConfirm: refetchPage,
    });
  };

  if (
    !showActionButtons &&
    !(TIME_CHANGE_STATUSES.includes(status) && originalTime) &&
    !canViewDetails
  )
    return null;

  return (
    <VerticalSpace size="small" align={isMobile ? 'start' : 'end'}>
      {showActionButtons &&
        withStopPropagation(
          ['click'],
          <Space size="small">
            {canAccept && (
              <Button
                icon={
                  <Icon type="check" width={10} height={10} offset={[0, -1]} />
                }
                onClick={isBuyer ? buyerConfirm : laConfirm}
                loading={loading}
                disabled={loading}
                data-cy="showing-accept-button"
              >
                Accept
              </Button>
            )}
            {canDeny && (
              <Button
                icon={<Icon type="x" width={14} height={14} offset={[0, 1]} />}
                onClick={isBuyer ? buyerDeny : laDeny}
                data-cy="showing-deny-button"
              >
                Deny
              </Button>
            )}
          </Space>,
        )}
      {TIME_CHANGE_STATUSES.includes(status) &&
        originalTime &&
        (isTourItem ? (
          <DescriptionText dataCy="showing-description-text">{`${
            listingContactTimeChange ? 'Suggested' : 'Requested'
          }: ${createFormatDateFunction('E, MMM d')(time)} • ${formatTimeStr(
            time,
          )} - ${formatTimeStr(timeEnd)}`}</DescriptionText>
        ) : (
          <DescriptionText dataCy="showing-description-text">{`Previous: ${createFormatDateFunction(
            'E, MMM d',
          )(originalTime)} • ${formatTimeStr(originalTime)} - ${formatTimeStr(
            originalTimeEnd,
          )}`}</DescriptionText>
        ))}
      {canViewDetails && (
        <Button
          onClick={e => {
            e.stopPropagation();
            history.push(`/appointments/${appointment.id}/action`);
          }}
          data-cy="showing-actions-button"
        >
          View Details
        </Button>
      )}
    </VerticalSpace>
  );
};

export default ActionsEntry;
